import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const WarehouseChallengesSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [selectedChallenge, setSelectedChallenge] = useState(0);

  const challenges = [
    {
      id: 0,
      problem: {
        en: "Material Misplacement",
        hi: "मैटेरियल गलत स्थान पर रखना"
      },
      description: {
        en: "Aluminum profiles stored in wrong locations, causing delays and inefficiencies",
        hi: "एल्यूमीनियम प्रोफाइल गलत स्थानों पर संग्रहीत, जिससे देरी और अक्षमता होती है"
      },
      solution: {
        en: "Smart location tracking with barcode scanning and automated alerts",
        hi: "बारकोड स्कैनिंग और स्वचालित अलर्ट के साथ स्मार्ट स्थान ट्रैकिंग"
      },
      impact: {
        en: "85% reduction in material search time",
        hi: "मैटेरियल खोजने के समय में 85% कमी"
      },
      icon: "MapPin"
    },
    {
      id: 1,
      problem: {
        en: "Inaccurate Stock Counts",
        hi: "गलत स्टॉक गणना"
      },
      description: {
        en: "Manual counting errors leading to production delays and customer dissatisfaction",
        hi: "मैन्युअल गिनती की त्रुटियां जो उत्पादन में देरी और ग्राहक असंतुष्टि का कारण बनती हैं"
      },
      solution: {
        en: "Real-time digital inventory tracking with automated cycle counting",
        hi: "स्वचालित चक्र गणना के साथ रियल-टाइम डिजिटल इन्वेंटरी ट्रैकिंग"
      },
      impact: {
        en: "99.9% inventory accuracy achieved",
        hi: "99.9% इन्वेंटरी सटीकता प्राप्त"
      },
      icon: "Calculator"
    },
    {
      id: 2,
      problem: {
        en: "Delayed Order Fulfillment",
        hi: "ऑर्डर पूर्ति में देरी"
      },
      description: {
        en: "Inefficient picking processes and poor warehouse layout optimization",
        hi: "अक्षम पिकिंग प्रक्रियाएं और खराब वेयरहाउस लेआउट अनुकूलन"
      },
      solution: {
        en: "Optimized picking routes and intelligent warehouse layout design",
        hi: "अनुकूलित पिकिंग रूट और बुद्धिमान वेयरहाउस लेआउट डिज़ाइन"
      },
      impact: {
        en: "60% faster order processing",
        hi: "60% तेज़ ऑर्डर प्रोसेसिंग"
      },
      icon: "Clock"
    },
    {
      id: 3,
      problem: {
        en: "Excess Inventory Costs",
        hi: "अतिरिक्त इन्वेंटरी लागत"
      },
      description: {
        en: "Overstocking and understocking due to poor demand forecasting",
        hi: "खराब मांग पूर्वानुमान के कारण अधिक स्टॉकिंग और कम स्टॉकिंग"
      },
      solution: {
        en: "AI-powered demand forecasting and automated reorder point optimization",
        hi: "AI-संचालित मांग पूर्वानुमान और स्वचालित पुनर्व्यवस्था बिंदु अनुकूलन"
      },
      impact: {
        en: "30% reduction in carrying costs",
        hi: "कैरिंग लागत में 30% कमी"
      },
      icon: "DollarSign"
    }
  ];

  const t = (text) => text[currentLanguage] || text.en;

  return (
    <section className="py-20 bg-gradient-to-b from-surface-50 to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {currentLanguage === 'en' ?'Warehouse Challenges We Solve' :'हमारे द्वारा हल की जाने वाली वेयरहाउस चुनौतियां'
            }
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {currentLanguage === 'en' ?'Discover how our WIS addresses the most common warehouse management challenges faced by aluminum manufacturers' :'जानें कि हमारा WIS एल्यूमीनियम निर्माताओं द्वारा सामना की जाने वाली सबसे आम वेयरहाउस प्रबंधन चुनौतियों को कैसे हल करता है'
            }
          </p>
        </div>

        {/* Interactive Problem-Solution Mapping */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Challenge Selection */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-primary mb-6">
              {currentLanguage === 'en' ? 'Common Challenges' : 'सामान्य चुनौतियां'}
            </h3>
            {challenges.map((challenge, index) => (
              <div
                key={challenge.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                  selectedChallenge === index
                    ? 'border-primary bg-primary-50 shadow-md'
                    : 'border-border bg-white hover:border-primary-300 hover:shadow-sm'
                }`}
                onClick={() => setSelectedChallenge(index)}
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${
                    selectedChallenge === index ? 'bg-primary text-white' : 'bg-surface-200 text-text-secondary'
                  }`}>
                    <Icon name={challenge.icon} size={20} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-primary">{t(challenge.problem)}</h4>
                    <p className="text-sm text-text-secondary mt-1">{t(challenge.description)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Solution Display */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-border">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full mb-4">
                <Icon name={challenges[selectedChallenge].icon} size={32} />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-2">
                {t(challenges[selectedChallenge].problem)}
              </h3>
              <p className="text-text-secondary">
                {t(challenges[selectedChallenge].description)}
              </p>
            </div>

            {/* Solution */}
            <div className="bg-success-50 rounded-lg p-6 mb-6">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                <h4 className="font-medium text-success">
                  {currentLanguage === 'en' ? 'Our Solution' : 'हमारा समाधान'}
                </h4>
              </div>
              <p className="text-sm text-text-secondary">
                {t(challenges[selectedChallenge].solution)}
              </p>
            </div>

            {/* Impact */}
            <div className="bg-gradient-to-r from-primary-50 to-info-50 rounded-lg p-6">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
                <h4 className="font-medium text-primary">
                  {currentLanguage === 'en' ? 'Expected Impact' : 'अपेक्षित प्रभाव'}
                </h4>
              </div>
              <p className="text-lg font-semibold text-primary">
                {t(challenges[selectedChallenge].impact)}
              </p>
            </div>

            {/* CTA */}
            <div className="text-center mt-6">
              <button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl">
                {currentLanguage === 'en' ? 'Learn More About This Solution' : 'इस समाधान के बारे में और जानें'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WarehouseChallengesSection;